import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { usePlayer } from '../../stores/PlayerContext';
import { useFavorite } from '../../stores/FavoriteContext';
import { RTHK_LIVE_STREAMS } from '../../services/rthk';
import {
  fetchPopularPrograms,
  fetchCurrentPlaying,
  type Program,
} from '../../services/rthkApi';

interface LiveProgramInfo {
  program: string;
  host: string;
}

export default function Home() {
  const { currentChannel, setChannel } = usePlayer();
  const { favorites } = useFavorite();

  const [popularPrograms, setPopularPrograms] = useState<Program[]>([]);
  const [liveProgramInfo, setLiveProgramInfo] = useState<
    Record<string, LiveProgramInfo>
  >({});
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [networkError, setNetworkError] = useState(false);

  /* =========================================================
   * LIVE PROGRAM
   * ========================================================= */

  const updateLiveProgramInfo = useCallback(() => {
    const channels = [
      'radio1',
      'radio2',
      'radio3',
      'radio4',
      'radio5',
    ];

    Promise.allSettled(
      channels.map((channelId) =>
        fetchCurrentPlaying(channelId).then((info) => {
          if (info) {
            setLiveProgramInfo((prev) => ({
              ...prev,
              [channelId]: {
                program: info.program || '',
                host: info.host || '',
              },
            }));
          }
        })
      )
    ).then(() => {
      setLastUpdate(new Date());
    });
  }, []);

  /* =========================================================
   * POPULAR PROGRAMS
   * ========================================================= */

  useEffect(() => {
    fetchPopularPrograms()
      .then((programs) => {
        setPopularPrograms(programs.slice(0, 4));
        setNetworkError(false);
      })
      .catch(() => {
        setNetworkError(true);
      });
  }, []);

  /* =========================================================
   * UPDATE PROGRAM
   * ========================================================= */

  useEffect(() => {
    updateLiveProgramInfo();

    const timer = setInterval(
      updateLiveProgramInfo,
      5 * 60 * 1000
    );

    return () => clearInterval(timer);
  }, [updateLiveProgramInfo]);

  /* =========================================================
   * LIVE PROGRAM EVENT
   * ========================================================= */

  useEffect(() => {
    const handleLiveUpdate = (e: CustomEvent) => {
      const { channelId, program, host } = e.detail;

      if (channelId) {
        setLiveProgramInfo((prev) => ({
          ...prev,
          [channelId]: {
            program: program || '',
            host: host || '',
          },
        }));

        setLastUpdate(new Date());
      }
    };

    window.addEventListener(
      'live-program-updated',
      handleLiveUpdate as EventListener
    );

    return () => {
      window.removeEventListener(
        'live-program-updated',
        handleLiveUpdate as EventListener
      );
    };
  }, []);

  /* =========================================================
   * PLAYBACK STOPPED
   * ========================================================= */

  useEffect(() => {
    const handlePlaybackStopped = () => {
      setChannel(null);
    };

    window.addEventListener(
      'playback-stopped',
      handlePlaybackStopped
    );

    return () => {
      window.removeEventListener(
        'playback-stopped',
        handlePlaybackStopped
      );
    };
  }, [setChannel]);

  /* =========================================================
   * LIVE CHANNELS
   * ========================================================= */

  const liveChannels = [
    {
      id: 'radio1',
      name: '香港電台第一台',
      nameEn: 'Radio 1',
      desc: '新聞、財經、時事',
      frequency: 'FM 92.6MHz',
      color:
        'bg-gradient-to-r from-[#210b0f] via-[#321017] to-[#1a0a0d]',
      accent: '#ef4444',
    },
    {
      id: 'radio2',
      name: '香港電台第二台',
      nameEn: 'Radio 2',
      desc: '流行音樂、青年節目',
      frequency: 'FM 94.8MHz',
      color:
        'bg-gradient-to-r from-[#091522] via-[#0e1d31] to-[#09131f]',
      accent: '#38bdf8',
    },
    {
      id: 'radio3',
      name: '香港電台第三台',
      nameEn: 'Radio 3',
      desc: '國際新聞、財經、資訊',
      frequency: 'FM 97.9MHz',
      color:
        'bg-gradient-to-r from-[#170d24] via-[#241239] to-[#130a1e]',
      accent: '#a855f7',
    },
    {
      id: 'radio4',
      name: '香港電台第四台',
      nameEn: 'Radio 4',
      desc: '古典音樂、藝術、文化',
      frequency: 'FM 97.6MHz',
      color:
        'bg-gradient-to-r from-[#211b0b] via-[#30270e] to-[#191508]',
      accent: '#d4a72c',
    },
    {
      id: 'radio5',
      name: '香港電台第五台',
      nameEn: 'Radio 5',
      desc: '文化、教育、社區',
      frequency: 'AM 783kHz',
      color:
        'bg-gradient-to-r from-[#091914] via-[#10271f] to-[#081510]',
      accent: '#34d399',
    },
    {
      id: 'asia',
      name: '亞洲電台粵語台',
      nameEn: 'Asia Radio Cantonese',
      desc: '香港粵語廣播',
      frequency: 'FM',
      color:
        'bg-gradient-to-r from-[#1d0c19] via-[#2d1228] to-[#180a15]',
      accent: '#ec4899',
    },
  ];

  /* =========================================================
   * PLAY CHANNEL
   * ========================================================= */

  const playChannel = (
    channel: (typeof liveChannels)[number]
  ) => {
    if (currentChannel?.id === channel.id) {
      window.dispatchEvent(
        new Event('playback-stopped')
      );
      return;
    }

    const channelData = {
      id: channel.id,
      name: channel.name,
      nameEn: channel.nameEn,
      streamUrl:
        RTHK_LIVE_STREAMS[channel.id] || '',
      logo: '',
      description: channel.desc,
    };

    setChannel(channelData);

    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('trigger-autoplay')
      );
    }, 100);
  };

  /* =========================================================
   * CHANNEL NAME
   * ========================================================= */

  const getChannelName = (
    channelId: string
  ): string => {
    const channel = liveChannels.find(
      (c) => c.id === channelId
    );

    return channel?.name || channelId;
  };

  /* =========================================================
   * CYBERPUNK HOME
   * ========================================================= */

  return (
    <div className="min-h-screen bg-[#07090d] text-[#cbd5e1] p-4 sm:p-6 space-y-8">

      {/* =====================================================
       * HEADER
       * ===================================================== */}

      <div className="relative overflow-hidden border border-[#252b36] bg-[#0d1117] px-5 py-5 shadow-2xl">

        <div className="absolute left-0 top-0 h-full w-[3px] bg-[#ef4444]" />

        <div className="absolute right-0 top-0 h-[1px] w-1/3 bg-gradient-to-l from-[#ef4444] to-transparent" />

        <div className="absolute right-0 bottom-0 h-[1px] w-1/4 bg-gradient-to-l from-[#7c3aed] to-transparent" />

        <div className="relative flex items-center justify-between">

          <div>

            <div className="flex items-center gap-3">

              <span className="text-[#ef4444] text-sm">
                ◆
              </span>

              <h1 className="text-2xl sm:text-3xl font-black tracking-wider text-[#e5e7eb]">
                香港電台
              </h1>

            </div>

            <p className="text-xs sm:text-sm tracking-[0.25em] text-[#64748b] mt-2 uppercase">
              RTHK // RADIO NETWORK
            </p>

          </div>

          <div className="hidden sm:block text-right">

            <div className="text-[10px] tracking-widest text-[#475569]">
              SYSTEM
            </div>

            <div className="text-xs text-[#ef4444] font-mono mt-1">
              ● ONLINE
            </div>

          </div>

        </div>

      </div>

      {/* =====================================================
       * LIVE CHANNELS
       * ===================================================== */}

      <section>

        <div className="flex items-center justify-between mb-4">

          <h2 className="text-xl font-bold tracking-wide text-[#dbe2ea] flex items-center gap-3">

            <span className="text-[#ef4444] text-base">
              📻
            </span>

            直播頻道

            <span className="hidden sm:inline text-[10px] font-mono tracking-widest text-[#475569]">
              // LIVE FEED
            </span>

          </h2>

          {lastUpdate && (
            <span className="text-[10px] font-mono tracking-wider text-[#475569]">
              SYNC:{' '}
              {lastUpdate.toLocaleTimeString(
                'zh-HK',
                {
                  hour: '2-digit',
                  minute: '2-digit',
                }
              )}
            </span>
          )}

        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

          {liveChannels.map((channel) => {

            const isPlaying =
              currentChannel?.id === channel.id;

            return (
              <div
                key={channel.id}
                className={`group relative overflow-hidden bg-[#0d1117] border ${
                  isPlaying
                    ? 'border-[#ef4444]'
                    : 'border-[#242b36]'
                } shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#475569]`}
              >

                {/* 顶部频道色带 */}

                <div
                  className={`relative w-full h-24 ${channel.color} flex items-center justify-center border-b border-[#252b36]`}
                >

                  <div
                    className="absolute left-0 top-0 h-full w-[3px]"
                    style={{
                      backgroundColor:
                        channel.accent,
                    }}
                  />

                  <div className="absolute inset-0 opacity-20 bg-[linear-gradient(135deg,transparent_25%,rgba(255,255,255,0.08)_25%,rgba(255,255,255,0.08)_50%,transparent_50%,transparent_75%,rgba(255,255,255,0.08)_75%)] bg-[length:8px_8px]" />

                  <div className="relative text-center">

                    <div className="text-[10px] font-mono tracking-[0.3em] text-[#64748b] mb-1">
                      {channel.nameEn}
                    </div>

                    <span className="text-[#dbe2ea] text-xl sm:text-2xl font-black tracking-wide">
                      {channel.name}
                    </span>

                  </div>

                </div>

                {/* 卡片内容 */}

                <div className="p-4">

                  <div className="flex items-center justify-between">

                    <h3 className="font-bold text-[#dbe2ea]">
                      {channel.name}
                    </h3>

                    {isPlaying && (
                      <span className="text-[9px] font-mono tracking-wider text-[#ef4444]">
                        ● LIVE
                      </span>
                    )}

                  </div>

                  <p className="text-sm text-[#94a3b8] mt-1">
                    {channel.desc}
                  </p>

                  <div className="flex items-center gap-2 mt-2">

                    <span className="text-[10px] font-mono text-[#475569]">
                      FREQ
                    </span>

                    <span className="text-xs font-mono text-[#64748b]">
                      {channel.frequency}
                    </span>

                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      playChannel(channel)
                    }
                    className={`relative overflow-hidden mt-4 w-full py-2.5 font-bold text-sm tracking-wide border transition-all duration-300 ${
                      isPlaying
                        ? 'bg-[#3b1115] border-[#ef4444] text-[#f87171] shadow-[0_0_15px_rgba(239,68,68,0.15)]'
                        : 'bg-[#151a21] border-[#303844] text-[#cbd5e1] hover:bg-[#1b222b] hover:border-[#64748b]'
                    }`}
                  >

                    <span className="relative z-10">

                      {isPlaying
                        ? '🔊 正在收聽'
                        : '▶ 開始收聽'}

                    </span>

                  </button>

                  {liveProgramInfo[channel.id] && (
                    <div className="mt-3 border-l-2 border-[#7f1d1d] pl-3">

                      <div className="text-[9px] font-mono tracking-widest text-[#475569]">
                        NOW PLAYING
                      </div>

                      <p className="text-xs text-[#f87171] mt-1 font-medium truncate">
                        {
                          liveProgramInfo[
                            channel.id
                          ].program
                        }

                        {liveProgramInfo[
                          channel.id
                        ].host &&
                          ` — ${
                            liveProgramInfo[
                              channel.id
                            ].host
                          }`}
                      </p>

                    </div>
                  )}

                </div>

              </div>
            );
          })}

        </div>

      </section>

      {/* =====================================================
       * POPULAR PROGRAMS
       * ===================================================== */}

      <section>

        <div className="flex items-center justify-between mb-4">

          <h2 className="text-xl font-bold tracking-wide text-[#dbe2ea] flex items-center gap-3">

            <span className="text-[#8b5cf6] text-base">
              📋
            </span>

            熱門節目

            <span className="hidden sm:inline text-[10px] font-mono tracking-widest text-[#475569]">
              // PROGRAMS
            </span>

          </h2>

          <Link
            to="/programs"
            className="text-xs font-mono tracking-wider text-[#64748b] hover:text-[#a78bfa] transition-colors"
          >
            查看全部 →
          </Link>

        </div>

        {networkError ||
        popularPrograms.length === 0 ? (

          <div className="bg-[#0d1117] border border-[#252b36] text-center py-10">

            <div className="text-2xl opacity-40 mb-3">
              ⚠
            </div>

            <p className="text-[#94a3b8]">
              無法獲取熱門節目資訊
            </p>

            <p className="text-xs font-mono text-[#475569] mt-2">
              NETWORK ERROR // RETRY LATER
            </p>

          </div>

        ) : (

          <div className="space-y-3">

            {popularPrograms.map(
              (program: Program) => (

                <Link
                  key={program.id}
                  to={`/programs/${program.channelId}/${program.id}`}
                  className="group bg-[#0d1117] border border-[#252b36] flex gap-4 p-3 hover:bg-[#111720] hover:border-[#475569] transition-all duration-300"
                >

                  <div className="w-20 h-20 bg-[#151a21] border border-[#29313c] flex-shrink-0 flex items-center justify-center">

                    <span className="text-2xl opacity-70">
                      📻
                    </span>

                  </div>

                  <div className="flex-1 min-w-0">

                    <div className="flex items-start justify-between gap-3">

                      <h3 className="font-bold truncate text-[#dbe2ea] group-hover:text-[#f87171] transition-colors">
                        {program.title}
                      </h3>

                      <span className="hidden sm:block text-[9px] font-mono text-[#475569]">
                        OPEN →
                      </span>

                    </div>

                    <p className="text-sm text-[#7f8b99] mt-1 line-clamp-2">
                      {program.description}
                    </p>

                    <p className="text-[10px] font-mono text-[#475569] mt-2">
                      {getChannelName(
                        program.channelId
                      )}{' '}
                      //{' '}
                      {program.episodeCount || 0}{' '}
                      EPISODES
                    </p>

                  </div>

                </Link>
              )
            )}

          </div>

        )}

      </section>

      {/* =====================================================
       * FAVORITES
       * ===================================================== */}

      <section>

        <div className="flex items-center justify-between mb-4">

          <h2 className="text-xl font-bold tracking-wide text-[#dbe2ea] flex items-center gap-3">

            <span className="text-[#a78bfa] text-base">
              ⭐
            </span>

            我的收藏

          </h2>

          <Link
            to="/favorites"
            className="text-xs font-mono tracking-wider text-[#64748b] hover:text-[#a78bfa] transition-colors"
          >
            管理 →
          </Link>

        </div>

        {favorites.length === 0 ? (

          <Link
            to="/favorites"
            className="group block text-center py-10 bg-[#0d1117] border border-[#252b36] hover:border-[#475569] transition-all"
          >

            <div className="text-3xl opacity-40 mb-3">
              ⭐
            </div>

            <p className="text-[#94a3b8]">
              還沒有收藏節目
            </p>

            <p className="text-[10px] font-mono tracking-wider text-[#475569] mt-2">
              ADD PROGRAMS TO YOUR ARCHIVE
            </p>

          </Link>

        ) : (

          <Link
            to="/favorites"
            className="group block bg-[#0d1117] border border-[#252b36] hover:bg-[#111720] hover:border-[#475569] transition-all"
          >

            <div className="flex items-center justify-between py-5 px-4">

              <div className="flex items-center gap-4">

                <div className="w-12 h-12 bg-[#1a1524] border border-[#4c1d95] text-[#a78bfa] flex items-center justify-center text-xl">
                  ⭐
                </div>

                <div>

                  <h3 className="font-bold text-[#dbe2ea]">
                    已收藏 {favorites.length} 個節目
                  </h3>

                  <p className="text-xs font-mono text-[#64748b] mt-1">
                    YOUR PROGRAM ARCHIVE
                  </p>

                </div>

              </div>

              <span className="text-[#64748b] group-hover:text-[#a78bfa] transition-colors">
                →
              </span>

            </div>

          </Link>

        )}

      </section>

      {/* =====================================================
       * FOOTER LINE
       * ===================================================== */}

      <div className="pt-2">

        <div className="flex items-center gap-3">

          <div className="h-[1px] flex-1 bg-[#1e2630]" />

          <span className="text-[9px] font-mono tracking-[0.3em] text-[#374151]">
            RTHK // LIVE RADIO
          </span>

          <div className="h-[1px] flex-1 bg-[#1e2630]" />

        </div>

      </div>

    </div>
  );
}
